import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate } from 'react-localize-redux';
import { connect } from 'react-redux';
import css from './ChartWidgetWrapper.css';
import { WIDGET_DATA_TYPES } from '../../../customDashboards/widgetDataTypes';
import OverflowingText from '../../foundations/OverflowingText';
import Tooltip from '../../molecules/Tooltip';
import Spinner from '../../widgets/Spinner';
import { EmptyState } from '../../widgets';
import { ACTION_TYPES, WIDGET_TYPE_TO_SCHEMA } from './consts';
import Icon, { IconSizes, IconVariants } from '../../foundations/Icon';
import { getAdminModeEnabled, getUserData } from '../../../selectors/user';
import useParseDimsMetricsAndFilters from '../../../customDashboards/widgetFields';
import { getFiltersWithTags } from '../../../utils/dashboardUtils';
import { OPERATOR_TYPES, WIDGET_TYPE_TOTALS } from '../../../utils/dashboardConsts';
import { formatFilterValues } from './utils';

function ChartWidgetWrapper({
    adminMode,
    userData,
    apiFields = null,
    widgetTitle,
    widgetType,
    widgetDataType,
    widgetFilters,
    showSpinner,
    showNoData,
    blockedFilters = [],
    showError,
    onMenuClick = () => {},
    onCloneClick = () => {},
    onDeleteClick = () => {},
    onExportClick = () => {},
    allowedActions = Object.values(ACTION_TYPES),
    children = null,
    withTopX = false,
}) {
    const [, , allFilters] = useParseDimsMetricsAndFilters(apiFields, adminMode, userData, widgetDataType);

    const filtersWithTags = useMemo(() => {
        if (!allFilters.length || !widgetFilters.length) {
            return [];
        }
        return getFiltersWithTags(widgetFilters, allFilters);
    }, [widgetFilters, allFilters]);

    const filtersSubtitle = useMemo(() => {
        if (!filtersWithTags.length) {
            return '';
        }

        const filters = filtersWithTags.filter(filter => filter.operator?.name === 'in');
        const excludedFilters = filtersWithTags.filter(filter => filter.operator?.name === 'not in');

        let subtitle = '';
        if (filters.length) {
            subtitle += 'Filters: ';
            subtitle += formatFilterValues(filters);
        }
        if (filters.length && excludedFilters.length) {
            subtitle += ' | ';
        }
        if (excludedFilters.length) {
            subtitle += ' Exclude: ';
            subtitle += formatFilterValues(excludedFilters);
        }

        return subtitle;
    }, [filtersWithTags]);

    return (
        <>
            <div className={css.gridWidgetHeader}>
                <div className={css.gridHeaderMain}>
                    <div className={classNames(css.gridHeaderTitle, { [css.withTopXFilters]: withTopX })}>
                        <div className={css.gridHeaderDataType}>
                            <Tooltip title={WIDGET_DATA_TYPES[widgetDataType].display_name}>
                                <span className={css.gridHeaderDataTypeIcon}>
                                    {WIDGET_DATA_TYPES[widgetDataType].icon}
                                </span>
                            </Tooltip>
                        </div>
                        <OverflowingText>{widgetTitle}</OverflowingText>
                    </div>
                    {filtersSubtitle && (
                        <Tooltip title={filtersSubtitle}>
                            <div className={classNames(css.gridHeaderFilters, { [css.withTopXFilters]: withTopX })}>
                                {filtersSubtitle}
                            </div>
                        </Tooltip>
                    )}
                </div>

                <div className={css.gridIconsSector} onMouseDown={e => e.stopPropagation()}>
                    {allowedActions.includes(ACTION_TYPES.TOGGLE_MENU) && (
                        <Tooltip
                            wrapperClass={css.gridIconContainer}
                            interactive
                            titleTranslationKey="STATIC.PAGES.DASHBOARD.ACTIONS.EDIT_WIDGET"
                            position="bottom"
                            distance={25}
                        >
                            <Icon
                                name="settings"
                                className={css.widgetIcon}
                                variant={IconVariants.LIGHT}
                                size={IconSizes.SMALL}
                                onClick={onMenuClick}
                            />
                        </Tooltip>
                    )}
                    {allowedActions.includes(ACTION_TYPES.CLONE) && (
                        <Tooltip
                            wrapperClass={css.gridIconContainer}
                            interactive
                            titleTranslationKey="STATIC.PAGES.DASHBOARD.ACTIONS.CLONE_WIDGET"
                            position="bottom"
                            distance={25}
                        >
                            <Icon
                                name="copy"
                                className={css.widgetIcon}
                                variant={IconVariants.LIGHT}
                                size={IconSizes.SMALL}
                                onClick={onCloneClick}
                            />
                        </Tooltip>
                    )}
                    {allowedActions.includes(ACTION_TYPES.EXPORT) && (
                        <Tooltip
                            wrapperClass={css.gridIconContainer}
                            interactive
                            titleTranslationKey="STATIC.PAGES.DASHBOARD.ACTIONS.EXPORT_WIDGET"
                            position="bottom"
                            distance={25}
                        >
                            <Icon
                                name="download"
                                className={css.widgetIcon}
                                variant={IconVariants.LIGHT}
                                size={IconSizes.SMALL}
                                onClick={onExportClick}
                            />
                        </Tooltip>
                    )}
                    {allowedActions.includes(ACTION_TYPES.DELETE) && (
                        <Tooltip
                            wrapperClass={css.gridIconContainer}
                            interactive
                            titleTranslationKey="STATIC.PAGES.DASHBOARD.ACTIONS.DELETE_WIDGET"
                            position="bottom"
                            distance={25}
                        >
                            <Icon
                                name="trash"
                                className={css.widgetIcon}
                                variant={IconVariants.LIGHT}
                                size={IconSizes.SMALL}
                                onClick={e => {
                                    e.stopPropagation();
                                    onDeleteClick();
                                }}
                            />
                        </Tooltip>
                    )}
                </div>
            </div>
            <div className={css.divider} />
            <div className={classNames(css.gridWidgetContent, { [css.totals]: widgetType === WIDGET_TYPE_TOTALS })}>
                {blockedFilters.length ? (
                    <div
                        className={classNames(
                            css.gridWidgetCenterContainer,
                            css.gridWidgetCenterNoData,
                            css.gridWidgetBlockedFilters
                        )}
                    >
                        <Icon disabled name="filter-off" variant={IconVariants.LIGHT} size={IconSizes.X_LARGE} />
                        <div>
                            <Translate id="STATIC.PAGES.DASHBOARD.WIDGET.QUERY_BLOCKED_FILTERS" />
                            <br />
                            <Translate
                                id="STATIC.PAGES.DASHBOARD.WIDGET.QUERY_BLOCKED_FILTERS_DETAILS"
                                data={{
                                    reportType: WIDGET_DATA_TYPES[widgetDataType].display_name,
                                    filterNames: blockedFilters.join(', '),
                                }}
                            />
                        </div>
                    </div>
                ) : (
                    <>
                        {showSpinner && (
                            <div className={css.gridWidgetCenterContainer}>
                                <Spinner show />
                            </div>
                        )}
                        {showNoData && (
                            <div className={css.gridWidgetCenterNoData}>
                                <EmptyState
                                    headerStyle={{ color: 'var(--gray500)', fontSize: '15px' }}
                                    customIcon={WIDGET_TYPE_TO_SCHEMA[widgetType]}
                                    header="STATIC.PAGES.DASHBOARD.WIDGET.QUERY_NO_DATA"
                                />
                            </div>
                        )}
                        {showError && (
                            <div className={css.gridWidgetCenterContainer}>
                                <EmptyState
                                    header={
                                        allowedActions.includes(ACTION_TYPES.TOGGLE_MENU)
                                            ? 'STATIC.PAGES.DASHBOARD.WIDGET.QUERY_ERROR'
                                            : 'STATIC.PAGES.DASHBOARD.WIDGET.QUERY_ERROR_QUICK_REPORT'
                                    }
                                    headerStyle={{ fontSize: 14 }}
                                />
                            </div>
                        )}
                    </>
                )}
                {children}
            </div>
        </>
    );
}

ChartWidgetWrapper.propTypes = {
    adminMode: PropTypes.bool.isRequired,
    userData: PropTypes.objectOf(PropTypes.any).isRequired,
    apiFields: PropTypes.objectOf(PropTypes.any),
    widgetTitle: PropTypes.string.isRequired,
    widgetType: PropTypes.string.isRequired,
    widgetDataType: PropTypes.string.isRequired,
    widgetFilters: PropTypes.arrayOf(
        PropTypes.shape({
            field: PropTypes.string.isRequired,
            operator: PropTypes.oneOf(OPERATOR_TYPES.map(x => x.name)).isRequired,
            values: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
        })
    ),
    showSpinner: PropTypes.bool.isRequired,
    showNoData: PropTypes.bool.isRequired,
    blockedFilters: PropTypes.arrayOf(PropTypes.string),
    showError: PropTypes.bool.isRequired,
    onMenuClick: PropTypes.func,
    onCloneClick: PropTypes.func,
    onDeleteClick: PropTypes.func,
    onExportClick: PropTypes.func,
    allowedActions: PropTypes.arrayOf(PropTypes.oneOf(Object.values(ACTION_TYPES))),
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    withTopX: PropTypes.bool,
};

export default connect((state, ownProps) => ({
    adminMode: getAdminModeEnabled(state),
    userData: getUserData(state),
    ...ownProps,
}))(memo(ChartWidgetWrapper));
